import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from './apiUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Alert, AlertDescription } from './components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './components/ui/dialog';
import { Trash2, CheckCircle, AlertCircle, Plus, Edit, Package, Coffee, X, ArrowUp, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const emptyRow = () => ({
  id: Date.now() + Math.random(),
  name: '',
  size: '',
  pack_quantity: '',
  invoice_price: '',
  mrp: '',
  markup: '',
  current_quantity: ''
});

function AccessoryManagement() {
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, accessory: null });
  const [editDialog, setEditDialog] = useState({ open: false, accessory: null });
  
  // Multiple rows for adding
  const [newRows, setNewRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  
  // Edit form data
  const [editFormData, setEditFormData] = useState({
    name: '',
    size: '',
    pack_quantity: '',
    invoice_price: '',
    mrp: '',
    markup: '',
    current_quantity: ''
  });

  useEffect(() => {
    fetchAccessories();
  }, []);
  
  // Drag and drop handlers
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, item) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedItem && draggedItem.id !== item.id) {
      setDragOverItem(item);
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetItem.id) {
      return;
    }

    const currentAccessories = [...accessories];
    const draggedIndex = currentAccessories.findIndex(item => item.id === draggedItem.id);
    const targetIndex = currentAccessories.findIndex(item => item.id === targetItem.id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }
    
    // Remove dragged item and insert at target position
    const [removed] = currentAccessories.splice(draggedIndex, 1);
    currentAccessories.splice(targetIndex, 0, removed);
    
    setAccessories(currentAccessories);
    setDraggedItem(null);
    setDragOverItem(null);
    
    // Save the new order
    saveSortOrder(currentAccessories);
  };

  const moveToTop = (itemId) => {
    const currentAccessories = [...accessories];
    const itemIndex = currentAccessories.findIndex(item => item.id === itemId);
    
    if (itemIndex <= 0) return;
    
    const [movedItem] = currentAccessories.splice(itemIndex, 1);
    currentAccessories.unshift(movedItem);
    
    setAccessories(currentAccessories);
    saveSortOrder(currentAccessories);
  };

  const saveSortOrder = async (sortedAccessories) => {
    try {
      const sortedIds = sortedAccessories.map(a => a.id);
      console.log('Saving sort order for IDs:', sortedIds);
      const response = await apiPut('/api/accessories/update-sort-order', { sortedIds });
      
      if (response.ok) {
        toast.success('Order saved');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to save sort order:', response.status, errorData);
        toast.error(errorData.message || `Failed to save order (${response.status})`);
      }
    } catch (error) {
      console.error('Error saving sort order:', error);
      toast.error(`Error: ${error.message}`);
    }
  };


  const fetchAccessories = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiGet('/api/accessories');
      if (!response.ok) throw new Error('Failed to fetch accessories');
      const data = await response.json();
      setAccessories(data);
    } catch (error) {
      console.error('Error fetching accessories:', error);
      setError('Failed to fetch accessories');
    } finally {
      setLoading(false);
    }
  };

  const handleNewRowChange = (rowId, field, value) => {
    setNewRows(rows => rows.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  const addMoreRows = () => {
    setNewRows(rows => [...rows, emptyRow(), emptyRow(), emptyRow()]);
  };

  const removeNewRow = (rowId) => {
    if (newRows.length <= 1) return;
    setNewRows(rows => rows.filter(row => row.id !== rowId));
  };

  const handleAddAccessories = async () => {
    setError('');
    
    // Filter valid rows (have name and mrp)
    const validRows = newRows.filter(row => row.name.trim() && row.mrp);
    
    if (validRows.length === 0) {
      toast.error('Please fill at least one row with Name and MRP');
      return;
    }
    
    setSaving(true);
    try {
      const accessoriesToAdd = validRows.map(row => ({
        name: row.name.trim(),
        size: row.size.trim() || null,
        pack_quantity: parseInt(row.pack_quantity) || 1,
        invoice_price: row.invoice_price ? parseFloat(row.invoice_price) : null,
        mrp: parseFloat(row.mrp),
        markup: row.markup ? parseFloat(row.markup) : 0,
        current_quantity: parseInt(row.current_quantity) || 0
      }));
      
      const response = await apiPost('/api/accessories/batch', { accessories: accessoriesToAdd });
      
      if (response.ok) {
        const result = await response.json();
        if (result.errors > 0) {
          toast.warning(`Added ${result.added} accessories. ${result.errors} failed: ${result.errorDetails.map(e => e.name).join(', ')}`);
        } else {
          toast.success(`Successfully added ${result.added} accessories!`);
        }
        // Reset to empty rows
        setNewRows([emptyRow(), emptyRow(), emptyRow()]);
        fetchAccessories();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to add accessories');
      }
    } catch (error) {
      console.error('Error adding accessories:', error);
      setError('Failed to add accessories');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (accessory) => {
    setEditDialog({ open: true, accessory });
    setEditFormData({
      name: accessory.name || '',
      size: accessory.size || '',
      pack_quantity: accessory.pack_quantity?.toString() || '1',
      invoice_price: accessory.invoice_price?.toString() || '',
      mrp: accessory.mrp?.toString() || '',
      markup: accessory.markup?.toString() || '0',
      current_quantity: accessory.current_quantity?.toString() || '0'
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateAccessory = async () => {
    if (!editFormData.name.trim() || !editFormData.mrp) {
      toast.error('Name and MRP are required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await apiPut(`/api/accessories/${editDialog.accessory.id}`, {
        name: editFormData.name.trim(),
        size: editFormData.size.trim() || null,
        pack_quantity: parseInt(editFormData.pack_quantity) || 1,
        invoice_price: editFormData.invoice_price ? parseFloat(editFormData.invoice_price) : null,
        mrp: parseFloat(editFormData.mrp),
        markup: editFormData.markup ? parseFloat(editFormData.markup) : 0,
        current_quantity: parseInt(editFormData.current_quantity) || 0
      });
      
      if (response.ok) {
        toast.success('Accessory updated successfully!');
        setEditDialog({ open: false, accessory: null });
        fetchAccessories();
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to update accessory');
      }
    } catch (error) {
      console.error('Error updating accessory:', error);
      toast.error('Failed to update accessory');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccessory = async () => {
    if (!deleteDialog.accessory) return;
    
    try {
      const response = await apiDelete(`/api/accessories/${deleteDialog.accessory.id}`);
      
      if (response.ok) {
        toast.success('Accessory deleted successfully!');
        setDeleteDialog({ open: false, accessory: null });
        fetchAccessories();
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to delete accessory');
      }
    } catch (error) {
      console.error('Error deleting accessory:', error);
      toast.error('Failed to delete accessory');
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="space-y-2 text-left">
        <h1 className="text-4xl font-bold text-left">Accessories</h1>
        <p className="text-base text-muted-foreground text-left">Manage cool drinks, water bottles, and other non-liquor items</p>
      </div>

      {/* Add New Accessories Card - Table Format */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add New Accessories
          </CardTitle>
          <CardDescription>Add multiple cool drinks, water bottles, snacks, or other items at once</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Name *</TableHead>
                  <TableHead className="w-[80px]">Size</TableHead>
                  <TableHead className="w-[80px]">Pack Qty</TableHead>
                  <TableHead className="w-[100px]">
                    <div>Pack Invoice (₹)</div>
                    <div className="text-xs font-normal text-muted-foreground">per case</div>
                  </TableHead>
                  <TableHead className="w-[80px]">
                    <div>MRP (₹) *</div>
                    <div className="text-xs font-normal text-muted-foreground">per unit</div>
                  </TableHead>
                  <TableHead className="w-[80px]">
                    <div>Markup (₹)</div>
                    <div className="text-xs font-normal text-muted-foreground">per unit</div>
                  </TableHead>
                  <TableHead className="w-[80px]">Stock</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {newRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="p-1">
                      <Input
                        value={row.name}
                        onChange={(e) => handleNewRowChange(row.id, 'name', e.target.value)}
                        placeholder="e.g., Coca-Cola"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        value={row.size}
                        onChange={(e) => handleNewRowChange(row.id, 'size', e.target.value)}
                        placeholder="500ml"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        min="1"
                        value={row.pack_quantity}
                        onChange={(e) => handleNewRowChange(row.id, 'pack_quantity', e.target.value)}
                        placeholder="1"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.invoice_price}
                        onChange={(e) => handleNewRowChange(row.id, 'invoice_price', e.target.value)}
                        placeholder="0"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.mrp}
                        onChange={(e) => handleNewRowChange(row.id, 'mrp', e.target.value)}
                        placeholder="0"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.markup}
                        onChange={(e) => handleNewRowChange(row.id, 'markup', e.target.value)}
                        placeholder="0"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        min="0"
                        value={row.current_quantity}
                        onChange={(e) => handleNewRowChange(row.id, 'current_quantity', e.target.value)}
                        placeholder="0"
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      {newRows.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeNewRow(row.id)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <div className="flex justify-between items-center mt-4">
            <Button variant="outline" onClick={addMoreRows} className="gap-2">
              <Plus className="h-4 w-4" />
              Add More Rows
            </Button>
            <Button onClick={handleAddAccessories} disabled={saving} className="gap-2">
              <CheckCircle className="h-4 w-4" />
              {saving ? 'Adding...' : 'Add Accessories'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accessories List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Coffee className="h-5 w-5" />
                Your Accessories ({accessories.length})
              </CardTitle>
              <CardDescription>Drag rows to reorder or use the ↑ button to move items to top</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : accessories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-16 w-16 text-muted-foreground mb-4" />
              <h4 className="text-lg font-semibold mb-2">No accessories added yet</h4>
              <p className="text-sm text-muted-foreground">Use the table above to add cool drinks, water bottles, and other items</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">S.No</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-center">Size</TableHead>
                    <TableHead className="text-center">Pack Qty</TableHead>
                    <TableHead className="text-right">
                      <div>Pack Invoice</div>
                      <div className="text-xs font-normal text-muted-foreground">per case</div>
                    </TableHead>
                    <TableHead className="text-right">
                      <div>MRP</div>
                      <div className="text-xs font-normal text-muted-foreground">per unit</div>
                    </TableHead>
                    <TableHead className="text-right">
                      <div>Markup</div>
                      <div className="text-xs font-normal text-muted-foreground">per unit</div>
                    </TableHead>
                    <TableHead className="text-right">
                      <div>Final Price</div>
                      <div className="text-xs font-normal text-muted-foreground">per unit</div>
                    </TableHead>
                    <TableHead className="text-center">Stock</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accessories.map((accessory, index) => {
                    const isDragging = draggedItem?.id === accessory.id;
                    const isDraggedOver = dragOverItem?.id === accessory.id;
                    return (
                      <TableRow 
                        key={accessory.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, accessory)}
                        onDragOver={(e) => handleDragOver(e, accessory)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, accessory)}
                        className={`
                          cursor-move
                          ${isDragging ? 'opacity-50' : ''}
                          ${isDraggedOver ? 'bg-accent' : ''}
                        `}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1">
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                            <span>{index + 1}</span>
                            <Button 
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              title="Move to top"
                              onClick={(e) => { e.stopPropagation(); moveToTop(accessory.id); }}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold">{accessory.name}</TableCell>
                        <TableCell className="text-center">{accessory.size || '-'}</TableCell>
                        <TableCell className="text-center">{accessory.pack_quantity || 1}</TableCell>
                        <TableCell className="text-right">{formatCurrency(accessory.invoice_price)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(accessory.mrp)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(accessory.markup || 0)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency((parseFloat(accessory.mrp) || 0) + (parseFloat(accessory.markup) || 0))}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={accessory.current_quantity > 0 ? "default" : "secondary"}>
                            {accessory.current_quantity || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(accessory)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteDialog({ open: true, accessory })}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => {
        if (!open) {
          setEditDialog({ open: false, accessory: null });
          setError('');
        }
      }}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Edit Accessory</DialogTitle>
            <DialogDescription>Update the accessory details</DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name *</label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Size</label>
                <Input
                  value={editFormData.size}
                  onChange={(e) => handleEditFormChange('size', e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Pack Quantity</label>
                <Input
                  type="number"
                  min="1"
                  value={editFormData.pack_quantity}
                  onChange={(e) => handleEditFormChange('pack_quantity', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Stock</label>
                <Input
                  type="number"
                  min="0"
                  value={editFormData.current_quantity}
                  onChange={(e) => handleEditFormChange('current_quantity', e.target.value)}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Pack Invoice (₹)</label>
                <p className="text-xs text-muted-foreground -mt-1">per case</p>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editFormData.invoice_price}
                  onChange={(e) => handleEditFormChange('invoice_price', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">MRP (₹) *</label>
                <p className="text-xs text-muted-foreground -mt-1">per unit</p>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editFormData.mrp}
                  onChange={(e) => handleEditFormChange('mrp', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Markup (₹)</label>
                <p className="text-xs text-muted-foreground -mt-1">per unit</p>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editFormData.markup}
                  onChange={(e) => handleEditFormChange('markup', e.target.value)}
                />
              </div>
            </div>
            
            {editFormData.mrp && (
              <div className="bg-muted p-3 rounded-md space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Final Selling Price (per unit)</span>
                  <span className="text-lg font-bold">
                    ₹{((parseFloat(editFormData.mrp) || 0) + (parseFloat(editFormData.markup) || 0)).toFixed(2)}
                  </span>
                </div>
                {editFormData.invoice_price && editFormData.pack_quantity && (
                  <>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Cost per unit</span>
                      <span>₹{(parseFloat(editFormData.invoice_price) / parseInt(editFormData.pack_quantity)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Profit per unit</span>
                      <span className="text-green-600 font-medium">
                        ₹{(((parseFloat(editFormData.mrp) || 0) + (parseFloat(editFormData.markup) || 0)) - (parseFloat(editFormData.invoice_price) / parseInt(editFormData.pack_quantity))).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditDialog({ open: false, accessory: null });
              setError('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAccessory} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, accessory: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Accessory</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.accessory?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, accessory: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteAccessory}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

export default AccessoryManagement;
